/**
 * Composant pour la gestion des contrats de mandat des bénéficiaires
 * Permet de générer, envoyer et suivre les contrats de mandat
 */

import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import {
  creerContratMandat,
  envoyerContratMandat,
  obtenirStatutMandat,
  verifierMandatExistant,
  genererMandatsEnMasse,
  telechargerPDFMandat
} from '../services/mandateContractService';
import './BeneficiaryMandatePanel.css';

const BeneficiaryMandatePanel = () => {
  const [beneficiaires, setBeneficiaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBeneficiaire, setSelectedBeneficiaire] = useState(null);
  const [statusMandat, setStatusMandat] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'generate', 'send', 'view', 'mass'
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [massGenerationResult, setMassGenerationResult] = useState(null);

  // Charger les bénéficiaires au montage du composant
  useEffect(() => {
    chargerBeneficiaires();
  }, []);

  /**
   * Charge la liste des bénéficiaires actifs
   */
  const chargerBeneficiaires = async () => {
    try {
      setLoading(true);
      const clientsRef = collection(db, 'clients');
      const q = query(clientsRef, where('status', '==', 'ACTIVE'));
      const snapshot = await getDocs(q);

      const beneficiairesData = [];
      for (const docSnap of snapshot.docs) {
        const beneficiaire = {
          id: docSnap.id,
          ...docSnap.data()
        };

        // Vérifier le statut du mandat
        try {
          const { existe, mandat } = await verifierMandatExistant(db, beneficiaire.id);
          beneficiaire.mandatExiste = existe;
          beneficiaire.mandatStatus = mandat?.status || 'non_genere';
          beneficiaire.mandatId = mandat?.id || null;
        } catch (err) {
          console.error('Erreur vérification mandat:', err);
          beneficiaire.mandatExiste = false;
          beneficiaire.mandatStatus = 'non_genere';
        }

        beneficiairesData.push(beneficiaire);
      }

      setBeneficiaires(beneficiairesData);
      setLoading(false);
    } catch (err) {
      console.error('Erreur chargement bénéficiaires:', err);
      setError('Erreur lors du chargement des bénéficiaires');
      setLoading(false);
    }
  };

  /**
   * Génère un contrat de mandat pour un bénéficiaire
   */
  const handleGenererMandat = async (beneficiaire) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const userId = auth.currentUser.uid;
      const result = await creerContratMandat(db, beneficiaire.id, userId);

      if (result.success) {
        setSuccess(`Contrat de mandat créé avec succès pour ${beneficiaire.firstName} ${beneficiaire.lastName}`);
        await chargerBeneficiaires(); // Recharger la liste
        setShowModal(false);
      }
    } catch (err) {
      console.error('Erreur génération mandat:', err);
      setError(err.message || 'Erreur lors de la génération du contrat');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Envoie un contrat de mandat par email
   */
  const handleEnvoyerMandat = async (beneficiaire) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      if (!beneficiaire.email) {
        throw new Error('Le bénéficiaire n\'a pas d\'adresse email');
      }

      if (!beneficiaire.mandatId) {
        throw new Error('Aucun contrat de mandat trouvé. Veuillez d\'abord générer le contrat.');
      }

      const result = await envoyerContratMandat(
        db,
        beneficiaire.id,
        beneficiaire.mandatId,
        auth
      );

      if (result.success) {
        setSuccess(`Contrat envoyé par email à ${beneficiaire.email}`);
        await chargerBeneficiaires();
        setShowModal(false);
      }
    } catch (err) {
      console.error('Erreur envoi mandat:', err);
      setError(err.message || 'Erreur lors de l\'envoi du contrat');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Télécharge le PDF du contrat de mandat
   */
  const handleTelechargerPDF = async (beneficiaire) => {
    try {
      setLoading(true);
      setError(null);

      await telechargerPDFMandat(db, beneficiaire.id);
      setSuccess('PDF téléchargé avec succès');
    } catch (err) {
      console.error('Erreur téléchargement PDF:', err);
      setError(err.message || 'Erreur lors du téléchargement du PDF');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Génère les contrats en masse pour tous les bénéficiaires sans mandat
   */
  const handleGenerationEnMasse = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const userId = auth.currentUser.uid;
      const result = await genererMandatsEnMasse(db, userId);

      setMassGenerationResult(result);
      setSuccess(`Génération en masse terminée : ${result.generes} contrats créés, ${result.deja_existants} déjà existants, ${result.erreurs} erreurs`);
      await chargerBeneficiaires();
    } catch (err) {
      console.error('Erreur génération en masse:', err);
      setError(err.message || 'Erreur lors de la génération en masse');
    } finally {
      setLoading(false);
      setShowModal(false);
    }
  };

  /**
   * Affiche le modal de confirmation
   */
  const showConfirmationModal = (beneficiaire, type) => {
    setSelectedBeneficiaire(beneficiaire);
    setModalType(type);
    setShowModal(true);
  };

  /**
   * Retourne le badge de statut approprié
   */
  const getStatusBadge = (status) => {
    const badges = {
      'non_genere': { text: 'Non généré', class: 'status-non-genere' },
      'draft': { text: 'Brouillon', class: 'status-draft' },
      'pending_signature': { text: 'En attente de signature', class: 'status-pending' },
      'signed': { text: 'Signé', class: 'status-signed' },
      'archived': { text: 'Archivé', class: 'status-archived' }
    };

    const badge = badges[status] || badges['non_genere'];
    return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
  };

  /**
   * Ferme le modal et réinitialise les états
   */
  const closeModal = () => {
    setShowModal(false);
    setSelectedBeneficiaire(null);
    setModalType('');
  };

  /**
   * Confirme l'action du modal
   */
  const confirmModalAction = async () => {
    if (!selectedBeneficiaire) return;

    switch (modalType) {
      case 'generate':
        await handleGenererMandat(selectedBeneficiaire);
        break;
      case 'send':
        await handleEnvoyerMandat(selectedBeneficiaire);
        break;
      case 'mass':
        await handleGenerationEnMasse();
        break;
      default:
        break;
    }
  };

  if (loading && beneficiaires.length === 0) {
    return (
      <div className="mandate-panel-loading">
        <div className="spinner"></div>
        <p>Chargement des bénéficiaires...</p>
      </div>
    );
  }

  return (
    <div className="beneficiary-mandate-panel">
      <div className="panel-header">
        <h2>Gestion des Contrats de Mandat</h2>
        <p className="panel-description">
          Générez et envoyez les contrats de mandat pour vos bénéficiaires
        </p>
      </div>

      {/* Messages d'alerte */}
      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="alert-close">×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✅</span>
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="alert-close">×</button>
        </div>
      )}

      {/* Barre d'actions */}
      <div className="actions-bar">
        <button
          className="btn btn-primary"
          onClick={() => showConfirmationModal(null, 'mass')}
          disabled={loading}
        >
          <span className="btn-icon">📦</span>
          Génération en masse
        </button>
        <button
          className="btn btn-secondary"
          onClick={chargerBeneficiaires}
          disabled={loading}
        >
          <span className="btn-icon">🔄</span>
          Actualiser
        </button>
      </div>

      {/* Statistiques */}
      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-value">{beneficiaires.length}</div>
          <div className="stat-label">Bénéficiaires actifs</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {beneficiaires.filter(b => b.mandatStatus === 'signed').length}
          </div>
          <div className="stat-label">Contrats signés</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {beneficiaires.filter(b => b.mandatStatus === 'pending_signature').length}
          </div>
          <div className="stat-label">En attente de signature</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {beneficiaires.filter(b => b.mandatStatus === 'non_genere').length}
          </div>
          <div className="stat-label">Non générés</div>
        </div>
      </div>

      {/* Table des bénéficiaires */}
      <div className="table-container">
        <table className="beneficiaries-table">
          <thead>
            <tr>
              <th>Bénéficiaire</th>
              <th>Email</th>
              <th>Téléphone</th>
              <th>Statut du mandat</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {beneficiaires.map((beneficiaire) => (
              <tr key={beneficiaire.id}>
                <td>
                  <div className="beneficiary-info">
                    <span className="beneficiary-name">
                      {beneficiaire.firstName} {beneficiaire.lastName}
                    </span>
                  </div>
                </td>
                <td>{beneficiaire.email || '-'}</td>
                <td>{beneficiaire.phone || beneficiaire.phoneNumber || '-'}</td>
                <td>{getStatusBadge(beneficiaire.mandatStatus)}</td>
                <td>
                  <div className="action-buttons">
                    {beneficiaire.mandatStatus === 'non_genere' && (
                      <button
                        className="btn-action btn-generate"
                        onClick={() => showConfirmationModal(beneficiaire, 'generate')}
                        title="Générer le contrat"
                        disabled={loading}
                      >
                        📝 Générer
                      </button>
                    )}

                    {(beneficiaire.mandatStatus === 'draft' ||
                      beneficiaire.mandatStatus === 'pending_signature') && (
                      <>
                        <button
                          className="btn-action btn-send"
                          onClick={() => showConfirmationModal(beneficiaire, 'send')}
                          title="Envoyer par email"
                          disabled={loading || !beneficiaire.email}
                        >
                          📧 Envoyer
                        </button>
                        <button
                          className="btn-action btn-download"
                          onClick={() => handleTelechargerPDF(beneficiaire)}
                          title="Télécharger le PDF"
                          disabled={loading}
                        >
                          📥 PDF
                        </button>
                      </>
                    )}

                    {beneficiaire.mandatStatus === 'signed' && (
                      <button
                        className="btn-action btn-download"
                        onClick={() => handleTelechargerPDF(beneficiaire)}
                        title="Télécharger le contrat signé"
                        disabled={loading}
                      >
                        📥 Télécharger
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {beneficiaires.length === 0 && (
          <div className="empty-state">
            <p>Aucun bénéficiaire actif trouvé</p>
          </div>
        )}
      </div>

      {/* Modal de confirmation */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {modalType === 'generate' && 'Générer le contrat de mandat'}
                {modalType === 'send' && 'Envoyer le contrat par email'}
                {modalType === 'mass' && 'Génération en masse'}
              </h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <div className="modal-body">
              {modalType === 'generate' && selectedBeneficiaire && (
                <p>
                  Voulez-vous générer un contrat de mandat pour{' '}
                  <strong>{selectedBeneficiaire.firstName} {selectedBeneficiaire.lastName}</strong> ?
                </p>
              )}

              {modalType === 'send' && selectedBeneficiaire && (
                <>
                  <p>
                    Le contrat de mandat sera envoyé par email à{' '}
                    <strong>{selectedBeneficiaire.email}</strong>
                  </p>
                  <p className="modal-info">
                    Le bénéficiaire recevra un email avec le contrat en pièce jointe
                    et les instructions pour le signer électroniquement.
                  </p>
                </>
              )}

              {modalType === 'mass' && (
                <>
                  <p>
                    Cette action va générer automatiquement les contrats de mandat
                    pour tous les bénéficiaires actifs qui n'en ont pas encore.
                  </p>
                  <p className="modal-warning">
                    ⚠️ Cette opération peut prendre quelques minutes en fonction
                    du nombre de bénéficiaires.
                  </p>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={closeModal}
                disabled={loading}
              >
                Annuler
              </button>
              <button
                className="btn btn-primary"
                onClick={confirmModalAction}
                disabled={loading}
              >
                {loading ? 'En cours...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Résultat de la génération en masse */}
      {massGenerationResult && (
        <div className="mass-generation-result">
          <h4>Résultats de la génération en masse</h4>
          <div className="result-stats">
            <div className="result-stat success">
              <span className="result-value">{massGenerationResult.generes}</span>
              <span className="result-label">Créés</span>
            </div>
            <div className="result-stat info">
              <span className="result-value">{massGenerationResult.deja_existants}</span>
              <span className="result-label">Déjà existants</span>
            </div>
            <div className="result-stat error">
              <span className="result-value">{massGenerationResult.erreurs}</span>
              <span className="result-label">Erreurs</span>
            </div>
          </div>

          {massGenerationResult.details && massGenerationResult.details.length > 0 && (
            <div className="result-details">
              <h5>Détails :</h5>
              <ul>
                {massGenerationResult.details.map((detail, index) => (
                  <li key={index} className={`detail-${detail.status}`}>
                    <strong>{detail.nom}</strong> : {detail.status}
                    {detail.erreur && <span className="detail-error"> ({detail.erreur})</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <button
            className="btn btn-secondary"
            onClick={() => setMassGenerationResult(null)}
          >
            Fermer
          </button>
        </div>
      )}
    </div>
  );
};

export default BeneficiaryMandatePanel;
